import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import { setBillingDetails,setCheckoutStep } from "@store/reducers/cart";

import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";

import {
  BackBtn,
  ButtonGroup,
  CheckboxBox,
  CheckboxLabel,
  CheckboxRow,
  FormRow,
  IconCircle,
  InputField,
  InputGroup,
  PrimaryBtn,
  SectionLabel,
  SlidePanelBody,
  SlidePanelFooter,
  StepHeaderWrap,
  StepSubtitle,
  StepTitle,
} from "./styles";
import { COUNTRIES } from "./types";

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
      stroke="#ebc28e"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M14 2V8H20" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 13H8" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 17H8" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 9H8" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  zipCode?: string;
  country?: string;
}

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6L5 9L10 3" stroke="#3c2d23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownTrigger = styled.div<{ $open: boolean; $error: boolean; $hasValue: boolean }>`
  width: 100%;
  padding: 14px 36px 14px 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid ${({ $error }) => ($error ? "#e47a6a" : "rgba(255, 255, 255, 0.2)")};
  border-radius: 8px;
  box-sizing: border-box;
  color: ${({ $hasValue }) => ($hasValue ? "#ffffff" : "rgba(255, 255, 255, 0.5)")};
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  line-height: 1.2;
  cursor: pointer;
  transition: border-color 0.2s;
  box-sizing: border-box;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%) ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
    width: 12px;
    height: 8px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23b9a089' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    transition: transform 0.2s;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #EDE0D1;
  border-radius: 12px;
  z-index: 20;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 320px;
`;

const CountrySearchSection = styled.div`
  padding: 8px;
  background: #EDE0D1;
  border-bottom: 2px solid #8B7460;
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
`;

const CountrySearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #8B7460;
  border-radius: 8px;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: #3a2616;
  background: #fff;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #8B7460;
  }

  &:focus {
    border-color: #3a2616;
  }
`;

const DropdownList = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 4px 0;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #8B7460;
    border-radius: 3px;
  }
`;

const DropdownItem = styled.div<{ $selected: boolean }>`
  padding: 9px 16px;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: ${({ $selected }) => ($selected ? "#3a2616" : "#8B7460")};
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(89, 65, 46, 0.08);
    color: #3a2616;
  }
`;

const BillingView: React.FC = () => {
  const dispatch = useAppDispatch();
  const billing = useAppSelector((state) => state.cart.billingDetails);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [emailAlerts, setEmailAlerts] = useState(false);
  const [shakeForm, setShakeForm] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    const sorted = [...COUNTRIES].sort();
    if (!q) return sorted;
    return sorted.filter((c) => c.toLowerCase().includes(q));
  }, [countrySearch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCountryOpen(false);
        setCountrySearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!billing.email) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email)) {
      errs.email = "Invalid email format";
    }
    if (!billing.firstName) errs.firstName = "First name is required";
    if (!billing.lastName) errs.lastName = "Last name is required";
    if (!billing.address) errs.address = "Address is required";
    if (!billing.zipCode) errs.zipCode = "ZIP code is required";
    if (!billing.country) errs.country = "Country is required";
    return errs;
  };

  const handleField = (field: string, value: string) => {
    dispatch(setBillingDetails({ [field]: value }));
    if (touched[field]) {
      const newErrors = validate();
      setErrors(newErrors);
    }
  };

  const handleFocus = (field: string) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field as keyof FormErrors];
      return next;
    });
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const isFormValid = billing.email && billing.firstName && billing.lastName && billing.address && billing.zipCode && billing.country && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email);

  const handleContinue = () => {
    const allTouched: Record<string, boolean> = {};
    ["email", "firstName", "lastName", "address", "zipCode", "country"].forEach((f) => {
      allTouched[f] = true;
    });
    setTouched(allTouched);

    const errs = validate();
    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      dispatch(setCheckoutStep("tip"));
    } else {
      setShakeForm(true);
      setTimeout(() => setShakeForm(false), 500);
    }
  };

  const handleBack = () => {
    dispatch(setCheckoutStep("checkout"));
  };

  return (
    <>
      <SlidePanelBody>
      <StepHeaderWrap>
        <IconCircle>
          <FileIcon />
        </IconCircle>
        <StepTitle>Billing Details</StepTitle>
        <StepSubtitle>This information will be used on your invoices and receipts</StepSubtitle>
      </StepHeaderWrap>

      <ShakeWrap $shake={shakeForm}>
      <SectionLabel>
        <span>Contact</span>
      </SectionLabel>

      <InputGroup>
        <InputField
          type="email"
          value={billing.email}
          onChange={(e) => handleField("email", e.target.value)}
          onFocus={() => handleFocus("email")}
          onBlur={() => handleBlur("email")}
          placeholder="Email address"
          $error={!!errors.email}
        />
      </InputGroup>

      <SectionLabel>
        <span>Name</span>
      </SectionLabel>

      <FormRow>
        <InputGroup>
          <InputField
            type="text"
            value={billing.firstName}
            onChange={(e) => handleField("firstName", e.target.value)}
            onFocus={() => handleFocus("firstName")}
            onBlur={() => handleBlur("firstName")}
            placeholder="First name"
            $error={!!errors.firstName}
          />
        </InputGroup>
        <InputGroup>
          <InputField
            type="text"
            value={billing.lastName}
            onChange={(e) => handleField("lastName", e.target.value)}
            onFocus={() => handleFocus("lastName")}
            onBlur={() => handleBlur("lastName")}
            placeholder="Last name"
            $error={!!errors.lastName}
          />
        </InputGroup>
      </FormRow>

      <SectionLabel>
        <span>Billing Address</span>
      </SectionLabel>

      <InputGroup>
        <InputField
          type="text"
          value={billing.address}
          onChange={(e) => handleField("address", e.target.value)}
          onFocus={() => handleFocus("address")}
          onBlur={() => handleBlur("address")}
          placeholder="Street address"
          $error={!!errors.address}
        />
      </InputGroup>

      <FormRow>
        <InputGroup>
          <InputField
            type="text"
            value={billing.zipCode}
            onChange={(e) => handleField("zipCode", e.target.value)}
            onFocus={() => handleFocus("zipCode")}
            onBlur={() => handleBlur("zipCode")}
            placeholder="ZIP / Postal code"
            $error={!!errors.zipCode}
          />
        </InputGroup>
        <InputGroup>
          <DropdownWrap ref={dropdownRef}>
            <DropdownTrigger
              $open={countryOpen}
              $error={!!errors.country}
              $hasValue={!!billing.country}
              onClick={() => { setCountryOpen(!countryOpen); handleFocus("country"); }}
            >
              {billing.country || "Select country"}
            </DropdownTrigger>
            {countryOpen && (
              <DropdownMenu>
                <CountrySearchSection>
                  <CountrySearchInput
                    autoFocus
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder="Search countries..."
                    onClick={(e) => e.stopPropagation()}
                  />
                </CountrySearchSection>
                <DropdownList>
                  {filteredCountries.length === 0 && (
                    <DropdownItem $selected={false} as="div">
                      No countries match &ldquo;{countrySearch}&rdquo;
                    </DropdownItem>
                  )}
                  {filteredCountries.map((c) => (
                    <DropdownItem
                      key={c}
                      $selected={billing.country === c}
                      onClick={() => {
                        handleField("country", c);
                        setCountryOpen(false);
                        setCountrySearch("");
                      }}
                    >
                      {c}
                    </DropdownItem>
                  ))}
                </DropdownList>
              </DropdownMenu>
            )}
          </DropdownWrap>
        </InputGroup>
      </FormRow>

      <SectionLabel>
        <span>Preferences</span>
      </SectionLabel>

      <CheckboxRow onClick={() => setEmailAlerts(!emailAlerts)}>
        <CheckboxBox $checked={emailAlerts}>
          <CheckIcon />
        </CheckboxBox>
        <CheckboxLabel>
          Send me emails about new released music and offers.
        </CheckboxLabel>
      </CheckboxRow>
      </ShakeWrap>
      </SlidePanelBody>

      <SlidePanelFooter>
        <ButtonGroup style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
          <BackBtn onClick={handleBack} style={{ flex: 1 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg> Back</BackBtn>
          <PrimaryBtn $dimmed={!isFormValid} onClick={handleContinue} style={{ flex: 2 }}><ButtonLabel glyph="arrowRight" muted={!isFormValid}>Continue</ButtonLabel></PrimaryBtn>
        </ButtonGroup>
      </SlidePanelFooter>
    </>
  );
};

export default BillingView;
